import React from 'react';
import { useRouter } from 'next/router';
import { OrderPayment } from '../models';
import { useNotify } from './useNotify';
import { useUser } from './useUser';
import { roles } from '../static';

interface PayableDetailsTableProps {
  data: OrderPayment[];
  restarValues: boolean;
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpenReport?: React.Dispatch<React.SetStateAction<boolean>>;
  setAmount?: React.Dispatch<React.SetStateAction<number>>;
  setOrderProductsIds: React.Dispatch<React.SetStateAction<string[]>>;
}

export function usePayableDetailsTable({
  data,
  restarValues,
  setIsOpen,
  setAmount,
  setOrderProductsIds,
}: PayableDetailsTableProps) {
  // Hooks
  const router = useRouter();
  const notify = useNotify();
  const [user] = useUser();

  // States
  const [orderPayments, setOrderPayments] = React.useState<
    { _id: string; price: number }[]
  >([]);
  const [payablesData, setPayablesData] = React.useState<OrderPayment[]>(data);
  const [searchText, setSearchText] = React.useState<string | number>('');

  // Handle functions
  const onClickRow = (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    e.preventDefault();
    const { id } = e.currentTarget.dataset;
    // We redirect to the detail of the order
    const path =
      user.userType !== roles.ally
        ? `/app/payable/${router.query.payableId}/${id}`
        : `/app/ally-receivable/${id}`;
    router.push(path);
  };

  const handleCheck = (orderPaymentItem: OrderPayment) => {
    const { _id, price } = orderPaymentItem.orderProduct;
    // We find the index of the item in the array
    const index = orderPayments.findIndex((item) => item._id === _id);
    if (index === -1) {
      setOrderPayments((prev) => [...prev, { _id, price }]);
    } else {
      setOrderPayments((prev) => prev.filter((item) => item._id !== _id));
    }
  };

  const handlePayAll = () => {
    let totalAmount = 0;
    if (orderPayments.length > 0) {
      const filteredOrders = data.filter((dataItem) =>
        orderPayments.some(
          (orderPaymetItem) => orderPaymetItem._id === dataItem.orderProduct._id
        )
      );

      const orderProducts = filteredOrders.map((item) => {
        const { orderProduct } = item;
        return {
          orderPayments: orderProduct,
          status: item.paymentStatus,
          id: orderProduct._id,
        };
      });

      // We calculate the total amount to pay
      totalAmount = orderProducts.reduce(
        (accumulator, item) =>
          accumulator + item.orderPayments.variant.agreedPrice,
        0
      );
      setAmount(totalAmount);
      // We set the orderProductsIds to pay
      const pendingOrderProductIds = orderProducts.map((item) => item.id);
      setOrderProductsIds(pendingOrderProductIds);
      // We open the modal
      setIsOpen(true);
      return;
    }
    // If there are no selected items, we pay all the pending items
    const orderDataProduct = data
      .map((item) => {
        const { orderProduct } = item;
        return {
          orderPayments: orderProduct,
          status: item.paymentStatus,
          id: orderProduct._id,
        };
      })
      .filter((order) => order.status === 'pending');

    // If there are selected items
    // We calculate the total amount to pay
    totalAmount = orderDataProduct.reduce(
      (accumulator, item) =>
        accumulator + item.orderPayments.variant.agreedPrice,
      0
    );
    setAmount(totalAmount);
    // We set the orderProductsIds to pay
    const orderProductsIds = orderDataProduct.map((item) => item.id);
    setOrderProductsIds(orderProductsIds);

    if (orderProductsIds.length === 0) {
      notify('No posee cuentas pendientes con este aliado', 'info');
      return;
    }
    // We open the modal
    setIsOpen(true);
  };

  // UseEffects
  React.useEffect(() => {
    setOrderPayments([]);
  }, [restarValues]);

  React.useEffect(() => {
    setPayablesData(data);
  }, [data]);

  return {
    // States
    orderPayments,
    setOrderPayments,
    payablesData,
    setPayablesData,
    searchText,
    setSearchText,

    // Handle functions
    onClickRow,
    handleCheck,
    handlePayAll,
  };
}
